<template>
  <div ref="root" class="labeled-title m-title pb-title">
    <h1 class="h-visually-hidden">{{ firstTitle.trim() }}</h1>
    <div
      v-for="(data, i) in content"
      ref="rootContainer"
      :key="i"
      :class="{ 'is-active': activeContainerIndex === i }"
    >
      <div
        ref="titles"
        class="title"
        :class="[{ 'is-first': i === 0 }, classes ? classes : 'h1-custom']"
      >
        <span ref="titlesContainer" class="labeled-title-outer">
          <span
            v-for="title in data.titles"
            :key="title.text"
            :data-position="title.position"
            :data-offset="title.offset"
          >
            <span :style="{ fontSize }" class="title__word">
              {{ title.text }}
            </span>
          </span>
        </span>
      </div>

      <div
        v-show="!ui.isSmallLarger && data.labels"
        :class="{ 'is-first': i === 0 }"
        class="labels-outer mt-title"
      >
        <ul v-show="data.labels" ref="labels" class="labels">
          <li
            v-for="(label, index) in data.labels"
            :key="index"
            class="info-label labels__item"
          >
            <!-- eslint-disable-next-line vue/no-v-html -->
            <div :data-label="label.label" v-html="label.text"></div>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { gsap } from 'gsap'
import { SplitText } from 'gsap/SplitText'
import ScrollTrigger from 'gsap/ScrollTrigger'
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'

import { useUiStore } from '@/stores/ui'
import { useResize } from '@/utils/aware'

gsap.registerPlugin(ScrollTrigger)

interface Elements {
  titles: HTMLElement[]
  labels: HTMLElement[]
}

export interface LabeledTitle {
  titles: { text: string; position: string; offset?: string }[]
  labels?: { text: string; label: string }[]
}

const props = defineProps({
  classes: {
    type: Array,
    default: null,
    required: false,
  },
  content: {
    type: Array as () => LabeledTitle[],
    required: true,
  },
  noanimation: {
    type: Boolean,
    default: false,
    required: false,
  },
})

const ui = useUiStore()

const root = ref({} as HTMLElement)
const changeInterval = 3000
let interval: ReturnType<typeof setInterval> | null = null

const firstTitle = computed(() =>
  props.content[0].titles.reduce((acc, curr) => `${acc} ${curr.text}`, '')
)

const getElements = (container: HTMLElement): Elements => {
  const smallLabels = Array.from(
    container.querySelectorAll('[data-label]')
  ) as HTMLElement[]
  const titles = Array.from(
    container.querySelectorAll('[data-position]')
  ) as HTMLElement[]

  return { titles, labels: smallLabels }
}

const updateTitles = (elements: Elements) => {
  elements.titles.forEach(title => {
    if (!title.dataset || !title.dataset.offset) {
      return
    }

    title.style.transform = `translateX(${title.dataset.offset})`
  })
}

const checkForTextAlignement = (x: string, label: HTMLElement) => {
  const textAlignment =
    parseInt(x.replace(/px|%/, ''), 10) < 50 ? 'is-text-right' : ''
  textAlignment && label.classList.add(textAlignment)
}

const labels = ref<HTMLElement[]>([])
const newLabels = ref<HTMLElement[][]>([])
const labelsCreated = ref(false)
const createLabels = (elements: Elements, index: number) => {
  newLabels.value[index] = []
  elements.labels.forEach(label => {
    if (!label.dataset || !label.dataset.label) {
      return
    }

    const [titleNumber, x, y] = label.dataset.label.split(' ')
    const title = elements.titles.find(
      title => title.dataset.position === titleNumber
    )

    if (!title) {
      return
    }

    const isXNumber = /px|%/.test(x)
    const isYNumber = /px|%/.test(y)

    if (isXNumber && !isYNumber) {
      checkForTextAlignement(x, label)
      label.style.left = x
      label.classList.add(y)
    } else if (isYNumber && !isXNumber) {
      label.style.top = y
      label.classList.add(x)
    } else if (isYNumber && isXNumber) {
      checkForTextAlignement(x, label)
      label.style.top = y
      label.style.left = x
    } else {
      label.classList.add(x, y)
    }

    label.classList.add('title_label')

    const newLabel = label.cloneNode(true)
    title.appendChild(newLabel)
    newLabels.value[index].push(newLabel as HTMLElement)
  })
  labelsCreated.value = true
}

const deleteLabels = (elements: Elements) => {
  elements.titles.forEach(title => {
    title.querySelectorAll('[data-label]').forEach(label => {
      label.remove()
    })
  })
  labelsCreated.value = false
  newLabels.value = []
}

const splits: Record<string, Element[]>[] = []
// const splits: { [key: string]: Element[] }[] = []
const addSplitTitles = (elements: Elements, index: number) => {
  let splitTitles = new SplitText(elements.titles, {
    type: 'chars',
    tag: 'span',
    specialChars: (char: string) => (/^\S\s\S/.test(char) ? 2 : 1),
    charsClass: 'split-container',
  })
  splitTitles = new SplitText(splitTitles.chars, {
    type: 'chars',
    tag: 'span',
    charsClass: 'split',
    specialChars: (char: string) => (/^\S\s/.test(char) ? 2 : 1),
  })

  splits[index] = { titles: splitTitles.chars }
}

let introTls: gsap.core.Timeline[] = []
const playIntro = (elements: Elements, index: number) => {
  const tl = gsap.timeline({
    paused: true,
  })

  tl.set(splits[index].titles, { willChange: 'transform' })
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  // tl.add(applyHeight)
  tl.fromTo(
    splits[index].titles,
    { xPercent: -110 },
    {
      xPercent: 0,
      stagger: 0.01,
    }
  )

  if (ui.isSmallLarger && newLabels.value[index].length) {
    const splitLabels = new SplitText(newLabels.value[index], {
      type: 'chars, lines',
      tag: 'span',
      charsClass: 'split-label',
      linesClass: 'split-line',
    })
    tl.fromTo(
      splitLabels.chars,
      { yPercent: 125 },
      {
        yPercent: 0,
        stagger: 0.005,
      },
      '>-0.25'
    )
    splits[index].labels = splitLabels.chars
  }
  tl.set(splits[index].titles, { clearProps: 'will-change' })

  return tl
}

let outroTls: gsap.core.Timeline[] = []
const playOutro = (elements: Elements, index: number) => {
  const tl = gsap.timeline({
    paused: true,
  })

  tl.to(splits[index].titles, {
    xPercent: 110,
    stagger: 0.01,
  })

  if (ui.isSmallLarger && splits[index].labels) {
    tl.to(
      splits[index].labels,
      {
        yPercent: 125,
        stagger: 0.005,
      },
      '0'
    )
  }

  return tl
}

const titles = ref<HTMLElement[]>([])
const titlesContainer = ref<HTMLElement[]>([])
const rootContainer = ref<HTMLElement[]>([])
const activeContainerIndex = ref(0)
// let activeContainer: HTMLElement

// const applyHeight = () => {
//   const newActiveContainer = titlesContainer.value[activeContainerIndex.value]
//   const hasDelay =
//     activeContainer &&
//     newActiveContainer.offsetHeight < activeContainer.offsetHeight
//   activeContainer = titlesContainer.value[activeContainerIndex.value]

//   return (
//     activeContainer &&
//     gsap.to(titles.value[0], {
//       delay: hasDelay ? 0.5 : 0,
//       duration: 0.5,
//       height: activeContainer.offsetHeight,
//     })
//   )
// }

const next = () => {
  const nextOutroTL = outroTls[activeContainerIndex.value]

  if (nextOutroTL) {
    nextOutroTL.progress(0)
    nextOutroTL.play()
  }

  activeContainerIndex.value += 1
  activeContainerIndex.value %= titlesContainer.value.length

  const nextIntroTl = introTls[activeContainerIndex.value]

  if (nextIntroTl) {
    nextIntroTl.progress(0)
    nextIntroTl.play()
  }
}

const start = () => {
  if (interval || props.noanimation === true || props.content.length === 1) {
    return
  }

  interval = setInterval(next, changeInterval)
}

const stop = () => {
  if (!interval) {
    return
  }

  clearInterval(interval)
  interval = null
}

const fontSize = ref('')
const setFontSize = () => {
  if (typeof window === 'undefined') {
    return
  }

  const maxWidth = root.value.offsetWidth
  const lines = root.value?.querySelectorAll(
    'span[data-position]'
  ) as unknown as HTMLElement[]
  let [largestLine] = lines

  lines.forEach(line => {
    if (line.offsetWidth > largestLine?.offsetWidth) {
      largestLine = line
    }
  })

  if (largestLine?.offsetWidth > maxWidth) {
    const fake = largestLine.cloneNode(true) as HTMLElement
    fake.classList.add('h1-custom')
    fake.style.opacity = '0'
    fake.style.position = 'absolute'
    root.value.appendChild(fake)

    const updateFontSize = () => {
      fake.style.fontSize = `${size}px`

      if (Math.abs(maxWidth - fake.scrollWidth) > 5) {
        size -= 1
        updateFontSize()

        return
      }

      fontSize.value = `${size}px`
      root.value.removeChild(fake)
    }

    let size = Number(getComputedStyle(fake).fontSize.replace('px', ''))
    fake.style.fontSize = `${size}px`

    if (fake.scrollWidth > maxWidth) {
      updateFontSize()
    }
  }
}

const init = () => {
  if (props.content.length > 1) {
    // eslint-disable-next-line no-new
    new ScrollTrigger({
      trigger: root.value,
      onEnter: () => start(),
      onEnterBack: () => start(),
      onLeave: () => stop(),
      onLeaveBack: () => stop(),
    })
  }

  rootContainer.value.forEach((container, index) => {
    // Order must be respected
    const elements = getElements(container)

    updateTitles(elements)
    addSplitTitles(elements, index)

    if (ui.isSmallLarger) {
      createLabels(elements, index)
    }

    introTls.push(playIntro(elements, index))
    outroTls.push(playOutro(elements, index))
  })

  setTimeout(() => {
    setFontSize()
    introTls[activeContainerIndex.value].play()
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        start()
      } else {
        stop()
      }
    })
  }, 300)
}

useResize(() => {
  // applyHeight()

  if (labelsCreated.value && !ui.isSmallLarger) {
    rootContainer.value.forEach(container => {
      const elements = getElements(container)
      deleteLabels(elements)
    })
  }

  if (!labelsCreated.value && ui.isSmallLarger) {
    introTls = []
    outroTls = []

    rootContainer.value.forEach((container, index) => {
      const elements = getElements(container)

      createLabels(elements, index)
      introTls.push(playIntro(elements, index))
      outroTls.push(playOutro(elements, index))
    })

    if (introTls.length) {
      introTls[activeContainerIndex.value].play()
    }
  }
})

onMounted(() => {
  init()
})

onBeforeUnmount(() => {
  stop()
  gsap.killTweensOf(introTls)
  gsap.killTweensOf(outroTls)
  introTls = []
  outroTls = []
})
</script>

<style lang="scss" scoped>
/* stylelint-disable no-descending-specificity */

.labeled-title {
  position: relative;
  border-bottom: 1px solid var(--c-foreground);
}

.labeled-title-outer {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  pointer-events: none;
}

.title {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  pointer-events: none;

  &.is-first {
    position: initial;
  }

  :deep([data-position]) {
    position: relative;
  }

  :deep([data-label]) {
    @extend %fw-normal;

    // prettier-ignore
    @include fluid(font-size,(xxs: 4px, xl: 9px));

    // prettier-ignore
    @include fluid(line-height,(xxs: 10px, xl: 12px));

    position: absolute;
    font-family: $ff-default;

    &.is-text-center {
      text-align: center;
    }

    &.is-text-right {
      text-align: right;
    }

    b {
      @extend %fw-semi;

      display: block;
    }

    &.top {
      // prettier-ignore
      @include fluid(top,(xxs: 8px, xxl: 17px));
    }

    &.bottom {
      // prettier-ignore
      @include fluid(bottom,(xxs: 10px, xxl: 26px));
    }

    &.start {
      right: calc(100% + 2rem);
      text-align: right;
    }

    &.end {
      left: calc(100% + 2rem);
    }

    &.bottom-center {
      // prettier-ignore
      top: 42%;
      right: 0;
      bottom: inherit;
      left: inherit;
    }

    &.letter-o {
      right: inherit;
      /* stylelint-disable-next-line declaration-no-important */
      left: 39% !important;

      .split-line {
        /* stylelint-disable-next-line declaration-no-important */
        display: flex !important;
        justify-content: center;
      }
    }
  }
}

.title__word {
  display: flex;
  color: var(--c-foreground);
  line-height: 1;

  :deep(.split-container) {
    overflow: hidden;
    text-transform: uppercase;

    &:first-child .split {
      /* stylelint-disable-next-line declaration-no-important */
      margin-left: 0 !important;
    }
  }

  :deep(.split) {
    /* stylelint-disable-next-line declaration-no-important */
    display: block !important;
    margin-left: -0.6rem;
    padding-left: 0.6rem;
    white-space: pre;
  }
}

.title_label {
  :deep(.split-line) {
    overflow: hidden;
    line-height: 1;
  }

  :deep(.split-label) {
    /* stylelint-disable-next-line declaration-no-important */
    display: inline-block !important;
    overflow: hidden;
  }
}

.labels-outer {
  // prettier-ignore
  @include fluid(bottom,(xxs: 20px, xxl: 80px));

  position: absolute;
  overflow: hidden;
  width: 100%;
  padding: 1rem 0;
  border: solid var(--c-foreground);
  border-width: 1px 0;
  opacity: 0;
  transition: opacity 0.15s;

  &.is-first {
    position: initial;
  }

  .is-active & {
    opacity: 1;
  }
}

.labels {
  display: flex;
  justify-content: space-between;
  overflow: auto;
  margin: 0 -1rem;
  padding: 0;
}

.labels__item {
  flex-shrink: 0;
  flex-basis: calc(25% - 2rem);
  margin: 1rem;
  list-style-type: none;
  white-space: nowrap;

  b {
    display: block;
  }
  &:nth-child(n + 4) {
    @include mq($until: s) {
      display: none;
    }
  }
}
</style>
